import PropTypes from 'prop-types';
import React from 'react';
import classNames from 'classnames';
import { connect } from 'react-redux';
import { get, getRulesFromState, shapedPropsWarning } from 'ctm-util-functions';

import { withStyleProxy } from '../../higher_order_components/with_style_proxy';
// eslint-disable-next-line import/no-cycle
import { getContent } from '../utils';

import styles from './text.css';

/**
 * Text React Component
 * @desc Text component that allows to create text using
 *       <p>, <h1>, <h2>, <h3>, <h4> and <span> html elements.
 *       Allows to use variations based on the corresponding css modules variant.
 * @param {Object} props React props
 * @param {Object} ref The forwarded ref that will be attached to the actual text element
 * @returns {_Text} a _Text component
 */
const _Text = (props, ref) => {
    // @todo remove once all shapedProps are gone
    shapedPropsWarning(props, 'Text');

    const { className, dataId, tagName, variant, rules, componentKey, stateProps } = props;
    // Prepare attributes for creating the element.
    const variantStyle = styles[variant] || null;
    const componentType = tagName || 'span';
    const classNameList = classNames(className || null, variantStyle) || null;
    const labelContent = getContent(props);
    const reactElementProps = { 'data-id': dataId, className: classNameList, ref };

    const allowedKeys = ['homeCoverAmount', 'homeCoverTitle', 'homeExcessLabel'];

    if (get(rules, 'visibility')) {
        if (!rules.visibility.result) {
            return null;
        }
    }

    if (allowedKeys.includes(componentKey)) {
        const insuredValue = stateProps ? JSON.parse(JSON.stringify(stateProps)).product.productMeta.homeExcess.insuredValue : {};

        if (componentKey === 'homeExcessLabel' && insuredValue === '0') {
            return null;
        }

        if (insuredValue === '0') {
            return null;
        }
    }

    /**
     * Render the component.
     * When transferring props to a child component, you should ensure that you are not accidentally
     * forwarding props that were intended to be interpreted by the parent component
     * Only standard DOM attributes should be used when creating a native DOM node.
     * Otherwise it would cause warnings like "Unknown props `visualProps` on <span> tag.
     * More info here: https://facebook.github.io/react/warnings/unknown-prop.html
     * @returns {Object} - Returns the Text React Component.
     */
    return React.createElement(componentType, reactElementProps, labelContent);
};

_Text.propTypes = {
    dataId: PropTypes.string,
    /**
     * Tag name of the element
     * the tags bellow are specific for this component.
     */
    tagName: PropTypes.oneOf(['span', 'p', 'div', 'strong', 'label', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6']),
    /**
     * If a state key is provided, it will not use label or innerHTML
     */
    stateKey: PropTypes.string,

    /**
     * If you need to use a stateKey in Text but make it format HTML
     */
    format: PropTypes.oneOf(['html']),
    /**
     * A text label. If provided, it will not use stateKey or innerHTML.
     */
    label: PropTypes.string,
    /**
     * HTML string -- parses and returns sanitized HTML and injects with dangerouslySetInnerHTML
     * If provided, will not use label or stateKey. If looking for way to inject state variables into
     * a text string, see TextTemplate component.
     */
    innerHTML: PropTypes.string,
    /**
     * Append some string (not a component) to the end of a string
     */
    suffix: PropTypes.string,
    /**
     * Prepend some string (not a component) to the start of a string
     */
    prefix: PropTypes.string,
    /**
     * stateProps Property so we can extract text from
     * @todo: not sure what to do with this, seems to be the only shaped prop we need,
     * as we need to extract the value from it based on stateKey
     */
    stateProps: PropTypes.object,
    variant: PropTypes.string,
    className: PropTypes.string
};

const mapStateToProps = (state, props) => {
    /* PSA: for schema components that needs the freshest values from selectors, add an empty rule
     * This forces the component to rerender.
     * When this function gets executed, usually that means somewhere redux has state change
     * because of how the schemas are set up, we can't fine-tune components to rerender
     * for instance schemas like this:
     * ```
     * "selectors": {
     * "names": {
     *   "value": "selector:keyFromState",
     *  }
     * }
     * ```
     * won't get updated as there's nothing causing the component to update its prop.
     * But the current code ensures new prop of rules is passed so long there are rules, thus retriggering the component.
     *
     * Potentially we could also pass props for a new selectors, but we don't know if existing schema will break
     * they rely on current behaviour. Perhaps something for the future like all the other TODOs we have in Everest
     }
     */
    const rules = props.rules ? getRulesFromState(state, props) : null;
    return {
        rules
    };
};

// Apply the visual Props Proxy Higher Order Component.
const Text = withStyleProxy(connect(mapStateToProps, null, null, { forwardRef: true })(React.forwardRef(_Text)));

// Export the Enhanced component.
export { Text };

// Export the non decorated component in order to test it by itself.
export { _Text };
